/**
 * Which checkout rails a buyer is offered, resolved once and shared by every configurator flow.
 *
 * The caller-facing API is {@link resolvePaymentSurface}: it folds the rail computation and the
 * card-region check into a single self-describing {@link PaymentSurface}, so a caller reads
 * `surface.fallback` and never has to interpret an empty array itself. The `paymentMethodsFor` /
 * `regionAllowsCard` helpers below are internal (kept exported only for their unit tests).
 *
 * The rails are `region-allowed ∩ runtime-available`, ordered:
 *   1. The region's allowed methods ({@link regionRails}). The backend field
 *      (`region_data.owner_hub_payment_methods`, surfaced as {@link Country.ownerHubPaymentMethods})
 *      is authoritative WHEN PRESENT: 'spei' maps to the SPEI bank transfer, 'pix' to the gate2way
 *      PIX rail, 'card' to the Stripe card charge, and any other string is dropped. When that field is absent, empty, or resolves
 *      to nothing known, we fall back to the currency-based code map: an MXN-priced session offers
 *      SPEI + card, a BRL-priced one PIX + card, every other currency is card-only. Either way each
 *      local rail is then kept only when the session currency matches it ({@link RAIL_CURRENCY}), so
 *      the ['card'] kill switch on BRA is honoured (the field wins) and a foreign local rail is dropped.
 *   2. The runtime gate: the Stripe rail is kept only when a publishable key is actually live
 *      (`isStripeLive()`); SPEI has no runtime precondition today. So a card-allowing region
 *      without a Stripe key resolves to no rails, which {@link resolvePaymentSurface} reports as
 *      `fallback: 'demo'`.
 *
 * `fallback: 'demo'` means "a card region running without a Stripe key", i.e. a DEMO environment,
 * not a broken one: the caller renders the Stripe surface, whose own `isStripeLive()` switch renders
 * `DemoCheckout`. The genuinely-no-rail `fallback: 'error'` is defensive: `regionRails` never
 * returns `[]` today (both the backend field and the code map always yield at least one rail), so a
 * rail-less surface in practice always resolves to 'demo', never 'error'.
 *
 * The code map is CURRENCY-based: an MXN session offers BOTH rails (SPEI first, then card), every
 * other currency is card-only — so SPEI shows only while the session is priced in MXN and disappears
 * the moment the buyer switches to USD/BRL/etc., regardless of the operating region. With a live
 * Stripe key MXN presents SPEI and Stripe side by side; the runtime gate drops card in a keyless/demo
 * environment, leaving SPEI. This is the fallback path that is the only path until the backend ships
 * the field; `region_data.owner_hub_payment_methods` stays authoritative when present.
 *
 * Lives in the v0-mx checkout dir as the single cross-flow source (the retained SPEI home), imported
 * by v0 and v3 as well.
 */
import { COMMERCE_GATE_PROVIDER, GATE2WAY_PROVIDER, STRIPE_PROVIDER, type CheckoutProvider } from '@/utils/session-invoice';
import { isStripeLive } from '@/nova/pages/configurator-v0-mx/data/payments';
import type { Country } from '@/nova/pages/configurator-v3/lib/domain-types';

export interface PaymentMethodsArgs {
  /** Resolved mint/session currency. */
  currency: string;
  /** Operating-region code (handoff or saved countryCode). Retained for the args shape; rail
   *  resolution is CURRENCY-based (see {@link codeMap}) and no longer keys off the region. */
  or: string | null;
  /** Selected region view-model, when the catalog has loaded. */
  region: Country | null;
}

/** Backend method string → checkout rail. Unknown strings are absent here and get dropped. */
const RAIL_BY_METHOD: Record<string, CheckoutProvider> = {
  spei: COMMERCE_GATE_PROVIDER,
  pix: GATE2WAY_PROVIDER,
  card: STRIPE_PROVIDER,
};

/** The session currency a local rail can settle in. A rail absent here (Stripe) is currency-agnostic.
 *  Exported for its test and so a future local rail registers its currency in one place. */
export const RAIL_CURRENCY: Partial<Record<CheckoutProvider, string>> = {
  [COMMERCE_GATE_PROVIDER]: 'MXN',
  [GATE2WAY_PROVIDER]: 'BRL',
};

/**
 * The rails the REGION permits, before the runtime (Stripe-key) gate: the backend field when it
 * resolves to anything known, else the currency-based code map.
 */
function regionRails(args: PaymentMethodsArgs): CheckoutProvider[] {
  const { currency, region } = args;
  const mapped = (region?.ownerHubPaymentMethods ?? [])
    .map((method) => RAIL_BY_METHOD[method])
    .filter((rail): rail is CheckoutProvider => rail !== undefined);
  const source = mapped.length > 0 ? mapped : codeMap(currency);
  // Currency rule, applied AFTER source selection so the backend field stays authoritative: a local
  // rail whose currency does not match the session is dropped, never replaced by the code map.
  return source.filter((rail) => {
    const required = RAIL_CURRENCY[rail];
    return required == null || required === currency;
  });
}

/**
 * Internal helper (exported for its unit test). True when the region permits card regardless of a
 * live Stripe key. {@link resolvePaymentSurface} uses it to tell a rail-less surface apart: a card
 * region without a key is a DEMO environment, not a broken one.
 */
export function regionAllowsCard(args: PaymentMethodsArgs): boolean {
  return regionRails(args).includes(STRIPE_PROVIDER);
}

/**
 * Internal helper (exported for its unit test). Region-allowed ∩ runtime-available, ordered. MAY be
 * empty when a card-allowing region runs without a live Stripe key. Prefer {@link resolvePaymentSurface}
 * from callers: it folds this empty case together with {@link regionAllowsCard} into one
 * self-describing result, so no caller has to interpret an empty array.
 */
export function paymentMethodsFor(args: PaymentMethodsArgs): CheckoutProvider[] {
  // Runtime gate: keep the Stripe rail only when a publishable key is live.
  return regionRails(args).filter((rail) => rail !== STRIPE_PROVIDER || isStripeLive());
}

/** The checkout surface a flow should render, resolved once from the region + runtime rails. */
export interface PaymentSurface {
  /** The rails to offer in the picker. Empty ONLY when fallback is not 'none'. */
  methods: CheckoutProvider[];
  /** What to render: 'none' = show `methods`; 'demo' = card region with no live Stripe key
   *  (render the Stripe surface, which self-degrades to DemoCheckout); 'error' = genuinely no rail. */
  fallback: 'none' | 'demo' | 'error';
}

/**
 * The single caller-facing resolver. Folds the rail computation and the card-region check into one
 * self-describing {@link PaymentSurface} so callers switch on `fallback` instead of interpreting an
 * empty array via a second call. 'error' is reachable: a region field listing only a local rail whose
 * currency does not match the session (e.g. ['spei'] on a BRL session) leaves no rail and no card.
 */
export function resolvePaymentSurface(args: PaymentMethodsArgs): PaymentSurface {
  const methods = paymentMethodsFor(args);
  if (methods.length > 0) return { methods, fallback: 'none' };
  return { methods, fallback: regionAllowsCard(args) ? 'demo' : 'error' };
}

/** Pre-field fallback, CURRENCY-based: an MXN session offers BOTH SPEI and card (SPEI first); every
 *  other currency is card-only. SPEI therefore follows the pricing currency, not the operating region,
 *  so switching a MEX session's currency to USD/BRL hides SPEI. The runtime Stripe-key gate in
 *  {@link paymentMethodsFor} drops card in a keyless/demo environment, leaving an MXN session on SPEI. */
function codeMap(currency: string): CheckoutProvider[] {
  if (currency === 'MXN') return [COMMERCE_GATE_PROVIDER, STRIPE_PROVIDER];
  if (currency === 'BRL') return [GATE2WAY_PROVIDER, STRIPE_PROVIDER];
  return [STRIPE_PROVIDER];
}
